/** Reusable styled confirm/alert dialog — replaces native window.confirm() and alert() */

import { useEffect, useRef } from 'react'
import { AlertTriangle, Info, X } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useAppStore } from '../../stores/app-store'

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm?: () => void
  title: string
  description?: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'danger' | 'info'
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'info',
}: ConfirmDialogProps) {
  const { theme } = useAppStore()
  const isDark = theme === 'dark'
  const confirmRef = useRef<HTMLButtonElement>(null)
  const isAlert = !onConfirm

  useEffect(() => {
    if (open) setTimeout(() => confirmRef.current?.focus(), 50)
  }, [open])

  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  const Icon = variant === 'danger' ? AlertTriangle : Info
  const iconColor = variant === 'danger' ? 'text-red-400' : 'text-brand-400'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div
        className={cn(
          'relative w-full max-w-sm overflow-hidden rounded-2xl border shadow-2xl',
          isDark ? 'border-white/[0.08] bg-surface-2' : 'border-neutral-200 bg-white',
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={cn('flex items-center justify-between px-5 py-4 border-b', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
          <div className="flex items-center gap-2">
            <Icon className={cn('h-4 w-4', iconColor)} />
            <h2 className={cn('text-sm font-semibold', isDark ? 'text-neutral-100' : 'text-neutral-900')}>
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={cn('cursor-pointer rounded-md p-1', isDark ? 'text-neutral-500 hover:text-neutral-300' : 'text-neutral-400 hover:text-neutral-600')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {description && (
            <p className={cn('text-xs leading-relaxed', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
              {description}
            </p>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2">
            {!isAlert && (
              <button
                type="button"
                onClick={onClose}
                className={cn(
                  'cursor-pointer rounded-lg px-3 py-1.5 text-xs font-medium',
                  isDark ? 'text-neutral-400 hover:bg-surface-3' : 'text-neutral-500 hover:bg-neutral-100',
                )}
              >
                {cancelLabel}
              </button>
            )}
            <button
              ref={confirmRef}
              type="button"
              onClick={() => { onConfirm?.(); onClose() }}
              className={cn(
                'cursor-pointer rounded-lg px-4 py-1.5 text-xs font-medium text-white',
                variant === 'danger'
                  ? 'bg-red-600 hover:bg-red-500'
                  : 'bg-brand-600 hover:bg-brand-500',
              )}
            >
              {isAlert ? 'OK' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
